'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { isAddItemCartResponseSuccessful, useCart, useCustomAddressStore } from '@libs/common-next';
import type { Product, TailoredApiResponse } from '@libs/common-types';
import { getFulfillmentConfigurationError } from '../../product-details';
import { getMiniPdpAddItemRequest } from '../lib';
import type { MiniPdpForm } from '../types';
import { useMiniPdpDrawer } from './useMiniPdpDrawer';
const SUCCESS_RESET_DELAY = 3000;
export type UseMiniPdpAddToCartProps = Readonly<{
  product?: Product;
  tailored: TailoredApiResponse['data'] | null;
  isLoading: boolean;
}>;
export function useMiniPdpAddToCart({
  product,
  tailored,
  isLoading
}: UseMiniPdpAddToCartProps) {
  const {
    addItem
  } = useCart();
  const {
    clearErrors,
    formState,
    handleSubmit,
    setError,
    trigger
  } = useFormContext<MiniPdpForm>();
  const isOpen = useMiniPdpDrawer(state => state.isOpen);
  const productId = useMiniPdpDrawer(state_0 => state_0.productId);
  const customAddress = useCustomAddressStore(state_1 => state_1.address);
  const selectedSku = useWatch({
    name: 'selectedSku'
  });
  const fulfilmentMethod = useWatch({
    name: 'fulfilmentMethod'
  });
  const quantity = useWatch({
    name: 'quantity'
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const submitPromiseRef = useRef<Promise<void> | null>(null);
  const sessionRef = useRef(0);
  const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
  const hasCompleteAddress = Boolean(customAddress?.streetLine1.trim() && customAddress.city.trim() && customAddress.stateProvince.trim() && customAddress.zipPostalCode.trim());
  const hasBopisStore = Boolean(tailoredVariant?.BOPIS?.storeId);
  const hasSddValidation = Boolean(tailoredVariant?.SDD?.doorDashValidationSuccess && hasCompleteAddress);
  const configurationError = getFulfillmentConfigurationError({
    fulfillmentMethod: fulfilmentMethod,
    hasBopisStore,
    hasSddValidation
  });
  const request = useMemo(() => product ? getMiniPdpAddItemRequest({
    formData: {
      selectedSku,
      fulfilmentMethod,
      quantity
    },
    customAddress,
    product,
    tailored
  }) : null, [customAddress, fulfilmentMethod, product, quantity, selectedSku, tailored]);
  useEffect(() => {
    clearErrors('root.deliveryMethods');
    if (configurationError) {
      const errorPath = fulfilmentMethod === 'bopis' ? 'root.deliveryMethods.bopis' : 'root.deliveryMethods.sdd';
      setError(errorPath, {
        type: 'validate',
        message: configurationError
      });
    }
    void trigger('fulfilmentMethod');
  }, [clearErrors, configurationError, customAddress, fulfilmentMethod, hasBopisStore, hasCompleteAddress, hasSddValidation, setError, trigger]);
  useEffect(() => {
    sessionRef.current += 1;
    setIsSuccess(false);
  }, [fulfilmentMethod, isOpen, productId, selectedSku]);
  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => setIsSuccess(false), SUCCESS_RESET_DELAY);
    return () => clearTimeout(timer);
  }, [isSuccess]);
  const submitHandler = handleSubmit(async formData => {
    if (submitPromiseRef.current) {
      await submitPromiseRef.current;
      return;
    }
    if (!product) return;
    const payload = getMiniPdpAddItemRequest({
      customAddress,
      formData,
      product,
      tailored
    });
    if (!payload) return;
    setIsSuccess(false);
    const requestSession = sessionRef.current;
    const submitPromise = (async () => {
      try {
        const cart = await addItem(payload);
        if (!isAddItemCartResponseSuccessful(cart)) return;
        if (sessionRef.current === requestSession) {
          setIsSuccess(true);
        }
      } catch {
        // Error presentation is deferred; return the CTA to its retry-ready state.
      }
    })();
    submitPromiseRef.current = submitPromise;
    try {
      await submitPromise;
    } finally {
      if (submitPromiseRef.current === submitPromise) {
        submitPromiseRef.current = null;
      }
    }
  });
  return {
    disabled: !isOpen || isLoading || !formState.isValid || Object.keys(formState.errors).length > 0 || !request,
    isSubmitting: formState.isSubmitting,
    isSuccess,
    submitHandler
  };
}